import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  Download, 
  Users, 
  Eye, 
  Heart, 
  MessageSquare, 
  TrendingUp,
  Clock,
  BarChart3,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { reportsAPI } from '../api/reports';
import { 
  formatNumber, 
  formatCurrency, 
  formatPercent, 
  formatWatchHours,
  formatDateRange
} from '../utils/formatters';
import MetricCard from './reports/MetricCard';
import CreatorTable from './reports/CreatorTable';
import TimelineChart from './reports/TimelineChart';
import PlatformBreakdown from './reports/PlatformBreakdown';
import ContentTable from './reports/ContentTable';
import ExportModal from './reports/ExportModal';
import Footer from './Footer';
import DateRangePicker from './reports/DateRangePicker';

const ReportDashboard = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  // State
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const [activeTab, setActiveTab] = useState('overview');
  const [showExportModal, setShowExportModal] = useState(false);
  const [timelinePeriod, setTimelinePeriod] = useState('week');

  // Query params
  const queryParams = useMemo(() => ({
    startDate: dateRange.startDate?.toISOString(),
    endDate: dateRange.endDate?.toISOString()
  }), [dateRange]);

  // Fetch report data
  const { data: summary, isLoading: summaryLoading, error: summaryError } = useQuery({
    queryKey: ['campaign-summary', campaignId, queryParams],
    queryFn: () => reportsAPI.getCampaignSummary(campaignId, queryParams).then(r => r.data),
    enabled: !!campaignId
  });

  const { data: creatorsData } = useQuery({
    queryKey: ['campaign-creators', campaignId, queryParams],
    queryFn: () => reportsAPI.getCampaignCreators(campaignId, queryParams).then(r => r.data),
    enabled: !!campaignId
  });

  const { data: timelineData } = useQuery({
    queryKey: ['campaign-timeline', campaignId, queryParams, timelinePeriod],
    queryFn: () => reportsAPI.getCampaignTimeline(campaignId, { ...queryParams, period: timelinePeriod }).then(r => r.data),
    enabled: !!campaignId
  });

  const { data: platformsData } = useQuery({
    queryKey: ['campaign-platforms', campaignId, queryParams],
    queryFn: () => reportsAPI.getCampaignPlatforms(campaignId, queryParams).then(r => r.data),
    enabled: !!campaignId
  });

  // Loading state
  if (summaryLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-sc-pink" />
        <span className="ml-2 text-gray-600">Loading report...</span>
      </div>
    );
  }

  // Error state
  if (summaryError) {
    return (
      <div className="flex items-center justify-center h-96">
        <AlertCircle className="w-8 h-8 text-red-500" />
        <span className="ml-2 text-red-600">Failed to load report data</span>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'creators', label: 'Creators' },
    { id: 'content', label: 'Content' },
    { id: 'timeline', label: 'Timeline' },
    { id: 'platforms', label: 'Platforms' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {summary?.campaign?.name || 'Campaign Report'}
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                {summary?.campaign?.game && `${typeof summary.campaign.game === 'object' ? (summary.campaign.game?.title || summary.campaign.game?.name) : summary.campaign.game} • `}
                {summary?.campaign?.clientName && `${summary.campaign.clientName} • `}
                {formatDateRange(summary?.campaign?.startDate, summary?.campaign?.endDate)}
              </p>
            </div>
            
            <div className="mt-4 md:mt-0 flex items-center space-x-4">
              <DateRangePicker
                value={dateRange}
                onChange={setDateRange}
                campaignStart={summary?.campaign?.startDate}
                campaignEnd={summary?.campaign?.endDate}
              />
              
              <button
                onClick={() => navigate(`/client-reports/${campaignId}`)}
                className="inline-flex items-center px-4 py-2 bg-sc-pink text-white rounded-lg text-sm font-medium hover:bg-sc-pink/90 transition-colors"
              >
                📄 Client Reports
              </button>
              
              <button
                onClick={() => setShowExportModal(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                <Download className="w-4 h-4 mr-2" />
                Export Report
              </button>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="mt-6 border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-sc-pink text-sc-pink'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview Tab */}
        {activeTab === 'overview' && summary && (
          <div className="space-y-8">
            {/* Summary Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <MetricCard
                icon={Users}
                label="Total Reach"
                value={formatNumber(summary.totals.reach)}
                sublabel={`${summary.totals.creatorCount} creators`}
              />
              <MetricCard
                icon={Eye}
                label="Total Views"
                value={formatNumber(summary.totals.views)}
              />
              {summary.totals.youtubeViews > 0 && (
                <MetricCard
                  icon={Eye}
                  label="YouTube Views"
                  value={formatNumber(summary.totals.youtubeViews)}
                />
              )}
              {summary.totals.vodViews > 0 && (
                <MetricCard
                  icon={Eye}
                  label="VoD Views"
                  value={formatNumber(summary.totals.vodViews)}
                />
              )}
              {summary.totals.tiktokViews > 0 && (
                <MetricCard
                  icon={Eye}
                  label="TikTok Views"
                  value={formatNumber(summary.totals.tiktokViews)}
                />
              )}
              {summary.totals.instagramViews > 0 && (
                <MetricCard
                  icon={Eye}
                  label="Instagram Views"
                  value={formatNumber(summary.totals.instagramViews)}
                />
              )}
              <MetricCard
                icon={Heart}
                label="Total Likes"
                value={formatNumber(summary.totals.likes)}
              />
              <MetricCard
                icon={MessageSquare}
                label="Comments"
                value={formatNumber(summary.totals.comments)}
              />
              <MetricCard
                icon={TrendingUp}
                label="Engagement Rate"
                value={formatPercent(summary.totals.engagementRate)}
                highlight={summary.totals.engagementRate > 3}
                youtubeEstimate="engagement"
              />
              <MetricCard
                icon={BarChart3}
                label="Content Pieces"
                value={summary.totals.contentCount}
              />
            </div>

            {/* Streaming Metrics (if applicable) */}
            {(summary.totals.avgCcv > 0 || summary.totals.peakCcv > 0) && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <MetricCard
                  icon={Eye}
                  label="Average CCV"
                  value={formatNumber(summary.totals.avgCcv)}
                  color="purple"
                />
                <MetricCard
                  icon={TrendingUp}
                  label="Peak CCV"
                  value={formatNumber(summary.totals.peakCcv)}
                  color="purple"
                />
                <MetricCard
                  icon={Clock}
                  label="Watch Hours"
                  value={formatWatchHours(summary.totals.watchHours)}
                  color="purple"
                  youtubeEstimate="watchHours"
                />
                <MetricCard
                  icon={Eye}
                  label="VOD Views"
                  value={formatNumber(summary.totals.vodViews)}
                  color="purple"
                />
              </div>
            )}

            {/* ROI Section */}
            {summary.roi && (
              <div className="card-pink-accent bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  ROI & Spend Analysis
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                  <div>
                    <p className="text-sm text-gray-500">Total Spend</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatCurrency(summary.roi.totalSpend)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Cost Per View</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatCurrency(summary.roi.costPerView)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">CPM</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatCurrency(summary.roi.costPer1000Views)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Cost Per Engagement</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatCurrency(summary.roi.costPerEngagement)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Budget Used</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {summary.roi.budgetUtilization}%
                    </p>
                    <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-sc-pink h-2 rounded-full"
                        style={{ width: `${Math.min(summary.roi.budgetUtilization, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Platform & Timeline Preview */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {platformsData && (
                <PlatformBreakdown 
                  data={platformsData.platforms} 
                  compact 
                />
              )}
              {timelineData && (
                <TimelineChart 
                  data={timelineData.timeline}
                  period={timelinePeriod}
                  onPeriodChange={setTimelinePeriod}
                  compact
                />
              )}
            </div>

            {/* Top Creators Preview */}
            {creatorsData && (
              <div className="card-pink-accent bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Top Performers
                  </h3>
                  <button 
                    onClick={() => setActiveTab('creators')}
                    className="text-sm text-sc-pink hover:text-sc-pink/80"
                  >
                    View all →
                  </button>
                </div>
                <CreatorTable 
                  data={creatorsData.creators.slice(0, 5)} 
                  compact 
                />
              </div>
            )}
          </div>
        )}

        {/* Creators Tab */}
        {activeTab === 'creators' && creatorsData && (
          <CreatorTable data={creatorsData.creators} />
        )}

        {/* Content Tab */}
        {activeTab === 'content' && (
          <ContentTable 
            campaignId={campaignId} 
            dateRange={dateRange}
          />
        )}

        {/* Timeline Tab */}
        {activeTab === 'timeline' && timelineData && (
          <TimelineChart 
            data={timelineData.timeline}
            period={timelinePeriod}
            onPeriodChange={setTimelinePeriod}
          />
        )}

        {/* Platforms Tab */}
        {activeTab === 'platforms' && platformsData && (
          <PlatformBreakdown data={platformsData.platforms} />
        )}
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <ExportModal
          campaignId={campaignId}
          campaignName={summary?.campaign?.name}
          dateRange={dateRange}
          onClose={() => setShowExportModal(false)}
        />
      )}
      <Footer />
    </div>
  );
};

export default ReportDashboard;
